import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Inbox } from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { cn } from '../../../lib/utils';
import { apiService, ErrorEventNotificationSummary, ErrorEventSeverity } from '../../../services/api';
import { NOTIFICATION_POLL_INTERVAL_MS } from '../../../hooks/errorEventNotificationLogic';
import { SEVERITY_COLORS, SEVERITY_LABELS } from './badges';

/** Severities absteigend nach Dringlichkeit — Reihenfolge der Aufschlüsselung auf der Card. */
const SEVERITY_ORDER: ErrorEventSeverity[] = ['critical', 'very_high', 'high', 'medium', 'low'];

/**
 * Zwei Cards für die Dashboard-Übersicht: offene Error-Events und DLQ-Tiefe.
 * Pollt dieselbe Summary wie die Kopfzeilen-Glocke (beim Mount + alle 30 s),
 * feuert aber bewusst KEINE Desktop-Benachrichtigungen — das ist Aufgabe der
 * Glocke, sonst käme jede Meldung doppelt.
 *
 * Nur für Super-Admins rendern — die Summary-API ist Super-Admin-only.
 */
export function ErrorEventsSummaryCards() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<ErrorEventNotificationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const data = await apiService.getErrorEventNotificationSummary();
        setSummary(data);
        setError(null);
      } catch {
        // Kein Toast: die Übersicht pollt alle 30 s, Fehler-Toasts würden fluten.
        setError('Error-Event-Status konnte nicht geladen werden');
      } finally {
        setLoading(false);
      }
    };

    void fetchSummary();
    const interval = setInterval(() => void fetchSummary(), NOTIFICATION_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const openTotal = summary?.openTotal ?? 0;
  const dlqTotal = summary?.dlqTotal ?? 0;
  const dlqUnavailable = summary !== null && summary.dlqTotal === null;

  const severityBreakdown = SEVERITY_ORDER.map((severity) => ({
    severity,
    count: summary?.openBySeverity[severity] ?? 0,
  })).filter((entry) => entry.count > 0);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card
        className={cn('cursor-pointer transition-all hover:shadow-md', openTotal > 0 && 'border-red-400 border-2')}
        onClick={() => navigate('/error-events')}
      >
        <CardContent className="p-6">
          <div className="flex items-center space-x-3">
            <div className={cn('p-2 rounded-lg', openTotal > 0 ? 'bg-red-50' : 'bg-green-50')}>
              <AlertTriangle className={cn('w-5 h-5', openTotal > 0 ? 'text-red-600' : 'text-green-600')} />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-600">Offene Error-Events</p>
              <p className="text-2xl font-bold text-gray-900">{loading ? '...' : error ? '-' : openTotal}</p>
            </div>
            {severityBreakdown.length > 0 && (
              <div className="flex flex-wrap justify-end gap-1">
                {severityBreakdown.map(({ severity, count }) => (
                  <Badge key={severity} className={cn('text-xs', SEVERITY_COLORS[severity])}>
                    {count}× {SEVERITY_LABELS[severity]}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
        </CardContent>
      </Card>

      <Card
        className={cn('cursor-pointer transition-all hover:shadow-md', dlqTotal > 0 && 'border-red-400 border-2')}
        onClick={() => navigate('/error-events?tab=dlq')}
      >
        <CardContent className="p-6">
          <div className="flex items-center space-x-3">
            <div className={cn('p-2 rounded-lg', dlqTotal > 0 ? 'bg-red-50' : 'bg-green-50')}>
              <Inbox className={cn('w-5 h-5', dlqTotal > 0 ? 'text-red-600' : 'text-green-600')} />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-600">Dead Letter Queue</p>
              <p className="text-2xl font-bold text-gray-900">
                {loading ? '...' : error ? '-' : dlqUnavailable ? '—' : dlqTotal}
              </p>
            </div>
            {dlqUnavailable && <span className="text-xs text-muted-foreground">nicht erreichbar</span>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
